import Checkbox from "common/components/Checkbox";
import { localStorageKeys } from "config/localStorageKeys";
import { showMobile } from "helpers/adaptation";
import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTypedDispatch, useTypedSelector } from "store";
import { setGame } from "store/game/slice";
import {
  getExerciseByCource,
  getExerciseByCourceInteractiveBid,
  getExerciseByCourceMixBasicBidding,
  getExerciseByCourceMixInteractiveBidding
} from "store/users/thunkActions";
import PracticeBiddingAllExercisesMobile from "./PracticeBiddingAllExercisesMobile";
import TabMixCourses from "./TabMixCourses";
import { setSimpleField, setTriggerAlert } from "store/users/slice";
import BlockMixIcon from "assets/icons/bidding/blockMixBid.svg";
import { shuffleArray } from "common/helpers/arrayTools";

interface Props {
  searchData?: any;
  data?: any;
  searchText?: string;
}

const PracticeBiddingAllExercises = ({ searchText, data }: Props) => {
  const [searchParams] = useSearchParams();

  const getInitialTab = () => {
    const subTab = searchParams.get("tab");
    if (subTab === "bidding_interactive") {
      return "bidding_interactive";
    }
    if (subTab === "bid_play") {
      return "bid_play";
    }
    return "bidding";
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [listChecked, setListChecked] = useState<any[]>([]);
  const dispatch = useTypedDispatch();
  const level = useTypedSelector((store) => store.game.levels);

  useEffect(() => {
    const resLevels = localStorage.getItem("levels");
    const audit: number[] = [];
    if (resLevels) {
      const valLevel = JSON.parse(resLevels);
      if (valLevel?.ease) audit.push(10);
      if (valLevel?.medium) audit.push(20);
      if (valLevel?.hard) audit.push(30);
    }

    if (audit.length > 0 && listChecked.length > 0) {
      const updatedListChecked = listChecked.filter((checkedItem) =>
        audit.some((levelCode) => `${checkedItem?.Levels}`.includes(`${levelCode}`))
      );

      if (updatedListChecked.length !== listChecked.length) {
        setListChecked(updatedListChecked);
      }
    }
  }, [level, listChecked]);

  const searchData = useMemo(() => {
    if (searchText === "") {
      return [];
    }

    return data?.map((val: any) => {
      return val?.parent?.columns?.map((itemColumns) => {
        return itemColumns?.courses?.map((itemCourses) => {
          if (itemCourses?.Name?.toLowerCase()?.indexOf(searchText.toLocaleLowerCase()) !== -1) {
            return itemCourses;
          }
        });
      });
    });
  }, [data, searchText]);

  const sortedList = useMemo(() => {
    const basicBidsList = [];
    const interactiveBidsList = [];
    const bidPlayList = [];
    let dataBoofer = [...data];
    if (searchText) {
      dataBoofer = [...searchData];
    }

    dataBoofer?.forEach((val) => {
      if (searchText) {
        val?.forEach((item) => {
          item?.forEach((itemCourses) => {
            if (itemCourses?.IsInteractive) {
              interactiveBidsList.push(itemCourses);
              if (itemCourses?.BidPlay) {
                bidPlayList.push(itemCourses);
              }
            } else {
              basicBidsList.push(itemCourses);
            }
          });
        });
      } else {
        val?.parent?.columns?.forEach((item) => {
          item?.courses?.forEach((itemCourses) => {
            if (itemCourses?.IsInteractive) {
              interactiveBidsList.push(itemCourses);
              if (itemCourses?.BidPlay) {
                bidPlayList.push(itemCourses);
              }
            } else {
              basicBidsList.push(itemCourses);
            }
          });
        });
      }
    });
    return {
      basicBidsList,
      interactiveBidsList,
      bidPlayList
    };
  }, [data, searchData, searchText]);

  const onChecked = useCallback(
    (val: any, item: any) => {
      if (item.target.checked) {
        const boofer: any = [...listChecked];
        const findIndex = boofer.findIndex((val_find: any) => val_find?.Id === val?.Id);
        if (findIndex === -1) {
          boofer.push(val);
          setListChecked(boofer);
        }
      } else {
        const boofer = [...listChecked];
        const findIndex = boofer.findIndex((val_find) => val_find?.Id === val?.Id);
        if (findIndex !== -1) {
          boofer.splice(findIndex, 1);
          setListChecked(boofer);
        }
      }
    },
    [listChecked]
  );

  const navigate = useNavigate();

  const { subscriptionValid } = useTypedSelector((store) => store.user.client);

  const onSubmit = useCallback(async () => {
    if (!subscriptionValid) {
      return dispatch(
        setSimpleField({
          type: "blockMixCourses",
          value: true
        })
      );
    }

    localStorage.setItem(localStorageKeys.mixCoursesListID, JSON.stringify(listChecked));

    let res: any;
    let idCourseSelect = 0;

    if (activeTab === "bidding") {
      res = await dispatch(getExerciseByCource(listChecked[idCourseSelect]?.Id, level));
      if (!res?.Results?.length && listChecked?.length > 1) {
        idCourseSelect = idCourseSelect + 1;
        res = await dispatch(getExerciseByCource(listChecked[idCourseSelect]?.Id, level));
      }
    } else {
      res = await dispatch(
        getExerciseByCourceInteractiveBid(listChecked[idCourseSelect]?.Id, level)
      );
      if (!res?.Results?.length && listChecked?.length > 1) {
        idCourseSelect = idCourseSelect + 1;
        res = await dispatch(
          getExerciseByCourceInteractiveBid(listChecked[idCourseSelect]?.Id, level)
        );
      }
    }

    if (!res?.Results?.length) {
      return alert("This course is empty");
    }

    let arrResult: any[] = [];

    if (res?.Results?.length > 0) {
      arrResult = [...res.Results];
    }
    let resCourse;
    if (activeTab === "bidding") {
      resCourse = await dispatch(getExerciseByCourceMixBasicBidding());
    } else {
      resCourse = await dispatch(getExerciseByCourceMixInteractiveBidding());
    }
    const shuffleList = shuffleArray(resCourse?.Results);
    let booferArr = [];

    const resLevels = localStorage.getItem("levels");
    const audit: number[] = [];
    if (resLevels) {
      const valLevel = JSON.parse(resLevels);
      if (valLevel?.ease) audit.push(10);
      if (valLevel?.medium) audit.push(20);
      if (valLevel?.hard) audit.push(30);
    }

    if (audit.length) {
      shuffleList.forEach((val) => {
        if (audit.find((valFind) => `${val?.Data?.Level}`.indexOf(`${valFind}`) !== -1)) {
          booferArr.push(val);
        }
      });
    } else {
      booferArr = [...shuffleList];
    }
    localStorage.setItem("listMixBidding", JSON.stringify(booferArr));

    const data = {
      gameId: booferArr[0]?.Data?.Id,
      courseId: booferArr[0]?.Data?.CourseId,
      gameName: booferArr[0]?.Data?.Name,
      gameTitle: "תרגול משולב",
      mixCourses: listChecked,
      type: activeTab === "bidding" ? "bidding" : "interactive-bidding"
    };

    localStorage.setItem("bidding", JSON.stringify(data));
    dispatch(setGame(data));

    if (!arrResult?.length) {
      return dispatch(
        setTriggerAlert({
          show: true,
          title: "This course is empty"
        })
      );
    }

    if (activeTab === "bidding") {
      navigate(`/bidding?type=60&typeBidding=mix&tab=${activeTab}`);
    } else {
      navigate(`/bidding_interactive?type=60&typeBidding=mix&tab=${activeTab}`);
    }
  }, [activeTab, dispatch, listChecked, navigate, subscriptionValid, level]);

  const listToDisplay = useMemo(() => {
    switch (activeTab) {
      case "bidding":
        return sortedList.basicBidsList;
      case "bidding_interactive":
        return sortedList.interactiveBidsList;
      case "bid_play":
        return sortedList.bidPlayList;
      default:
        return [];
    }
  }, [activeTab, sortedList]);

  return (
    <>
      {showMobile && (
        <PracticeBiddingAllExercisesMobile
          {...{
            setActiveTab,
            activeTab,
            setListChecked,
            listChecked,
            sortedList,
            onChecked,
            onSubmit
          }}
        />
      )}
      {!showMobile && (
        <div className="PracticeByCourses-main">
          <div style={{ width: "100%" }}>
            <TabMixCourses
              onSelect={(val) => {
                setActiveTab(val);
                setListChecked([]);
              }}
              activeTab={activeTab}
            />
            <div className="container-count-active-course">
              <span>קורסים נבחרו: {listChecked.length || 0}</span>
            </div>
            <div className="container-list-mix">
              {listToDisplay.map((val) => {
                if (!val) {
                  return null;
                }
                const resLevels = localStorage.getItem("levels");
                const audit: number[] = [];
                if (resLevels) {
                  const valLevel = JSON.parse(resLevels);
                  if (valLevel?.ease) audit.push(10);
                  if (valLevel?.medium) audit.push(20);
                  if (valLevel?.hard) audit.push(30);
                }

                let isDisabled = false;

                if (
                  audit?.length > 0 &&
                  !audit.find((itemFind) => `${val?.Levels}`.indexOf(`${itemFind}`) !== -1)
                ) {
                  isDisabled = true;
                }
                return (
                  <div key={`${val?.Id}some${Math.random() * 10000}`} className="bidding-item-mix">
                    {!isDisabled && (
                      <Checkbox
                        checked={!!listChecked.find((val_find) => val_find?.Id === val?.Id)}
                        label={val?.Name}
                        onClick={(item) => onChecked(val, item)}
                      />
                    )}
                    {isDisabled && (
                      <div className="bidding-item-mix-blocked">
                        <img src={BlockMixIcon} />
                        <span
                          style={{
                            fontWeight: 500,
                            fontSize: 20,
                            color: "#78659E"
                          }}>
                          {val?.Name}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="bidding-container-button">
              <div
                className={`PracticeByTechniqueTab-btn ${
                  listChecked.length === 0 ? "PracticeByTechniqueTab-btn--disabled" : ""
                }`}
                onClick={listChecked.length > 0 ? onSubmit : undefined}>
                <span className="start-btn">התחילו לתרגל</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PracticeBiddingAllExercises;
